import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Gauge, Calendar, Tag, CheckCircle2, Car } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api, apiError, formatPrice, formatMiles, imgSrc } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const inputCls =
  "h-12 rounded-none border-white/15 bg-black/40 px-4 text-sm text-white placeholder:text-zinc-600 focus-visible:ring-trx-blue";

const TestDriveForm = ({ vehicle }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", preferred_date: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/test-drives", {
        ...form,
        vehicle_id: vehicle.id,
        vehicle_name: `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim}`.trim(),
      });
      setDone(true);
      toast.success("Test drive request received.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="border border-trx-blue/40 bg-card p-8 text-center" data-testid="test-drive-confirmation">
        <CheckCircle2 className="mx-auto h-10 w-10 text-trx-blue" />
        <h3 className="mt-4 font-display text-2xl font-bold text-white">You're On The List.</h3>
        <p className="mx-auto mt-3 max-w-sm text-sm text-zinc-400">
          We'll call to confirm your test drive of the {vehicle.year} {vehicle.make} {vehicle.model}.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-white/10 bg-card p-8" data-testid="test-drive-form">
      <div className="flex items-center gap-3">
        <Car className="h-5 w-5 text-trx-blue" />
        <h3 className="font-display text-2xl font-bold tracking-tight text-white">Book A Test Drive</h3>
      </div>
      <p className="mt-2 text-sm text-zinc-500">Get behind the wheel — we'll have it ready and warmed up.</p>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <Input required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="test-drive-name" />
        <Input required placeholder="Phone *" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="test-drive-phone" />
        <Input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="test-drive-email" />
        <Input type="date" value={form.preferred_date} onChange={set("preferred_date")} className={`${inputCls} text-zinc-300`} data-testid="test-drive-date" />
      </div>
      <Textarea
        placeholder="Anything we should know? (trade-in, timing, etc.)"
        value={form.message}
        onChange={set("message")}
        className="mt-4 min-h-[90px] rounded-none border-white/15 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus-visible:ring-trx-blue"
        data-testid="test-drive-message"
      />
      <button
        type="submit"
        disabled={submitting}
        data-testid="test-drive-submit"
        className="mt-6 bg-trx-blue px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:bg-white hover:text-black disabled:opacity-50"
      >
        {submitting ? "Submitting…" : "Book Test Drive"}
      </button>
    </form>
  );
};

export default function VehicleDetail() {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [image, setImage] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/vehicles/${id}`)
      .then((r) => {
        setVehicle(r.data);
        document.title = `${r.data.year} ${r.data.make} ${r.data.model} | TRX Auto Sales & Service Inc`;
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 pt-20" data-testid="vehicle-not-found">
        <h1 className="font-display text-4xl font-black text-white">Vehicle Not Found</h1>
        <Link to="/inventory" className="text-sm font-semibold uppercase tracking-widest text-trx-blue">
          ← Back to inventory
        </Link>
      </div>
    );
  }

  if (!vehicle) {
    return <div className="min-h-screen pt-20" data-testid="vehicle-loading" />;
  }

  const sold = vehicle.status === "Sold";

  const highlights = [
    { icon: Calendar, label: "Year", value: vehicle.year },
    { icon: Gauge, label: "Mileage", value: formatMiles(vehicle.mileage) },
    { icon: Tag, label: "Body", value: vehicle.body_type || "—" },
  ];

  return (
    <div className="pt-20" data-testid="vehicle-detail">
      <div className="mx-auto max-w-[1600px] px-6 py-10 md:px-10">
        <Link to="/inventory" data-testid="vehicle-back" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 transition-colors hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Back to Inventory
        </Link>
      </div>

      <section className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="grid gap-12 lg:grid-cols-5 lg:gap-16">
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="relative overflow-hidden border border-white/10"
            >
              <img
                src={imgSrc(vehicle.images?.[image] || vehicle.images?.[0])}
                alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                className={`aspect-[16/10] w-full object-cover ${sold ? "grayscale" : ""}`}
                data-testid="vehicle-main-image"
              />
              {sold && (
                <span className="absolute left-5 top-5 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-black">
                  Sold
                </span>
              )}
            </motion.div>
            {vehicle.images?.length > 1 && (
              <div className="mt-4 flex gap-4">
                {vehicle.images.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setImage(i)}
                    data-testid={`vehicle-thumb-${i}`}
                    className={`relative aspect-[16/10] w-28 overflow-hidden border transition-colors duration-300 ${
                      i === image ? "border-trx-blue" : "border-white/10 opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={imgSrc(src)} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <Reveal className="lg:col-span-2">
            {vehicle.featured && !sold && (
              <span className="mb-5 inline-block bg-trx-blue px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white">
                Featured Vehicle
              </span>
            )}
            <h1 className="font-display text-4xl md:text-5xl font-black leading-[0.95] tracking-tighter text-white" data-testid="vehicle-title">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </h1>
            {vehicle.trim && <p className="mt-3 text-lg text-zinc-400">{vehicle.trim}</p>}
            <p className="mt-6 font-display text-4xl font-black text-trx-blue" data-testid="vehicle-price">
              {formatPrice(vehicle.price)}
            </p>

            <div className="mt-8 grid grid-cols-3 gap-px border border-white/10 bg-white/10">
              {highlights.map((h) => (
                <div key={h.label} className="bg-trx-black p-4">
                  <h.icon className="h-4 w-4 text-trx-blue" />
                  <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500">{h.label}</p>
                  <p className="mt-1 font-display text-sm font-bold text-white md:text-base">{h.value}</p>
                </div>
              ))}
            </div>

            <p className="mt-8 text-sm md:text-base leading-relaxed text-zinc-400" data-testid="vehicle-description">
              {vehicle.description}
            </p>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/financing"
                data-testid="vehicle-financing-btn"
                className="group flex flex-1 items-center justify-center gap-3 bg-trx-blue px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:bg-white hover:text-black"
              >
                Apply For Financing <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="vehicle-contact-btn"
                className="flex flex-1 items-center justify-center gap-3 border border-white/25 px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:border-trx-blue hover:text-trx-blue"
              >
                Contact Us
              </Link>
            </div>

            <div className="mt-8 flex items-center gap-3 border border-white/10 bg-card p-4 text-sm text-zinc-400">
              <CheckCircle2 className="h-5 w-5 shrink-0 text-trx-blue" />
              Every TRX vehicle is inspected, serviced, and detailed before delivery.
            </div>
          </Reveal>
        </div>

        {!sold && (
          <div className="mt-16 max-w-3xl">
            <TestDriveForm vehicle={vehicle} />
          </div>
        )}
      </section>

      <div className="h-24 md:h-32" />
    </div>
  );
}
